import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRunning } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { db } from "@lib/firebase";
import { doc, getDoc } from "firebase/firestore";
import useEmblaCarousel from "embla-carousel-react";

import cardVertical from "@assets/card/vertical.jpg";
import cardClassic from "@assets/card/classic.jpg";
import cardShort from "@assets/card/short.jpg";
import cardLong from "@assets/card/long.jpg";
import cardFun from "@assets/card/fun.jpg";

import iconUtmb from "@assets/icons/utmb.png";
import iconItra0 from "@assets/icons/itra0.svg";
import iconUtmb20 from "@assets/icons/utmb20.png";
import iconItra1 from "@assets/icons/itra1.png";
import iconUtmb50 from "@assets/icons/utmb50.png";
import iconItra2 from "@assets/icons/itra2.png";
import iconItra3 from "@assets/icons/itra3.svg";
import iconUtmb100 from "@assets/icons/utmb100.svg";
import iconItra4 from "@assets/icons/itra4.png";

const getCategoryIcons = (categoryName, distance) => {
  switch (categoryName) {
    case "MOUNTAIN UPHILL (5K)":
    case "MOUNTAIN UPHILL (7K)":
      return {
        icon1Url: null,
        icon2Url: null,
        icon3Url: null,
      };
    case "MOUNTAIN CLASSIC (10K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb20,
        icon3Url: iconItra1,
      };
    case "MOUNTAIN CLASSIC JUNIOR (10K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb20,
        icon3Url: iconItra1,
      };
    case "SHORT TRAIL (42K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb50,
        icon3Url: iconItra3,
      };
    case "LONG TRAIL (75K)":
      return {
        icon1Url: iconUtmb,
        icon2Url: iconUtmb100,
        icon3Url: iconItra4,
      };
    default:
      return { icon1Url: null, icon2Url: null, icon3Url: null };
  }
};

const formatPrice = (price, foreignerPrice) => {
  const domesticPrice = price
    ? `Rp ${Number(price).toLocaleString("id-ID")} (Domestic)`
    : "-";
  const foreignPrice = foreignerPrice
    ? `Rp ${Number(foreignerPrice).toLocaleString("id-ID")} (International)`
    : "-";
  return `${domesticPrice} / ${foreignPrice}`;
};

const getCategoryImage = (categoryName) => {
  switch (categoryName) {
    case "MOUNTAIN UPHILL (5K)":
    case "MOUNTAIN UPHILL (7K)":
      return cardVertical;
    case "MOUNTAIN CLASSIC (10K)":
      return cardClassic;
    case "MOUNTAIN CLASSIC JUNIOR (10K)":
      return cardFun;
    case "SHORT TRAIL (42K)":
      return cardShort;
    case "LONG TRAIL (75K)":
      return cardLong;
    default:
      return cardVertical;
  }
};

// Fungsi helper untuk check tanggal (otomatis tutup jika lewat hari ini)
const isEventInFuture = (eventDateStr) => {
  if (!eventDateStr) return false;
  try {
    const eventDate = new Date(eventDateStr);
    const now = new Date();
    now.setHours(0, 0, 0, 0); // Reset jam untuk perbandingan harian
    eventDate.setHours(0, 0, 0, 0);
    return eventDate > now;
  } catch {
    return false;
  }
};

// Fungsi untuk mapping kategori ke URL slug
const getCategorySlug = (categoryName) => {
  switch (categoryName) {
    case "MOUNTAIN UPHILL (5K)":
    case "MOUNTAIN UPHILL (7K)":
      return "7-vertical";
    case "MOUNTAIN CLASSIC (10K)":
      return "10-classic";
    case "MOUNTAIN CLASSIC JUNIOR (10K)":
      return "10-classic-junior";
    case "SHORT TRAIL (42K)":
      return "42-short";
    case "LONG TRAIL (75K)":
      return "75-long";
    default:
      return categoryName.toLowerCase().replace(/\s+/g, "-");
  }
};

// Container variants untuk animasi staggered
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1,
    },
  },
};

// Card variants untuk animasi smooth
const cardVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

export const SectionKategori = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const [emblaRef] = useEmblaCarousel({
    loop: false,
    align: "start",
    dragFree: true,
    containScroll: "trimSnaps",
  });

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const eventRef = doc(db, "semuaKegiatan", "SEATRC");
        const eventDoc = await getDoc(eventRef);

        if (eventDoc.exists()) {
          const data = eventDoc.data();
          const rawCategories = data.categories || [];

          // Ambil status dari level event
          const eventStatus = data.status || "Registrasi Ditutup";
          const isEventOpen = eventStatus === "Registrasi Dibuka";

          // Map categories dengan order baru
          const mappedCategories = rawCategories.map((cat, index) => {
            const trimmedCategoryName = cat.categoryName.trim();
            let order;
            switch (trimmedCategoryName) {
              case "MOUNTAIN UPHILL (5K)":
              case "MOUNTAIN UPHILL (7K)":
                order = 1;
                break;
              case "MOUNTAIN CLASSIC (10K)":
                order = 2;
                break;
              case "MOUNTAIN CLASSIC JUNIOR (10K)":
                order = 3;
                break;
              case "SHORT TRAIL (42K)":
                order = 4;
                break;
              case "LONG TRAIL (75K)":
                order = 5;
                break;
              default:
                order = 6 + index;
            }
            const mappedCategory = {
              distance: cat.distance,
              categoryName: trimmedCategoryName,
              elevationGain: cat.elevationGain,
              cot: cat.cot,
              price: cat.price,
              foreignerPrice: cat.foreignerPrice || cat.price,
              date: cat.date,
              // Pakai status event + check tanggal
              isRegistrationOpen: isEventOpen && isEventInFuture(cat.date),
              order,
            };
            return mappedCategory;
          });

          // Sort by order
          const sortedCategories = mappedCategories.sort(
            (a, b) => a.order - b.order
          );

          setCategories(sortedCategories);
        } else {
          setError("Dokumen Jakarta Open Trail Run tidak ditemukan");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Deteksi lebar layar untuk mengaktifkan Embla Carousel di bawah 1024px
  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth < 1024);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (loading || error) {
    return (
      <section id="kategori" className="w-full py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto text-center">
            <p className="text-slate-600 text-lg">
              {loading ? "Loading categories..." : error || "An error occurred"}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="kategori"
      className="w-full py-10 relative z-0 bg-cover bg-center bg-fixed"
      style={{
        backgroundImage: "url('/bg/bghero11.jpg')",
      }}
    >
      <div className="absolute inset-0 bg-white z-0"></div>
      <div className="container mx-auto px-2 sm:px-4 lg:px-6 relative z-10">
        <div className="max-w-[1440px] mx-auto">
          <div className="text-center">
            <motion.h3
              className="text-3xl md:text-5xl mb-8 lg:mb-4 font-black text-gray-800 mt-2"
              initial={{ opacity: 0, y: -15 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            >
              RACE CATEGORIES
            </motion.h3>
          </div>

          {isSmallScreen ? (
            <div className="overflow-hidden px-2 sm:px-4" ref={emblaRef}>
              <motion.div
                className="flex flex-col gap-4 sm:flex-row sm:gap-4"
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {categories.map((category) => {
                  const icons = getCategoryIcons(
                    category.categoryName,
                    category.distance
                  );
                  const categoryImage = getCategoryImage(category.categoryName);
                  const categorySlug = getCategorySlug(category.categoryName);
                  return (
                    <motion.div
                      key={`${category.distance}-${category.categoryName}`}
                      variants={cardVariants}
                      className="min-w-[90%] w-full sm:min-w-[240px] sm:flex-1 sm:flex-shrink-0"
                    >
                      <Link
                        to={`/SEATRC/${categorySlug}`}
                        className="group relative z-0 block"
                      >
                        <motion.div
                          className="relative rounded-2xl overflow-hidden bg-cover bg-center w-full h-72 z-0"
                          style={{
                            backgroundImage: `url(${categoryImage})`,
                            aspectRatio: "2/1",
                          }}
                          whileHover={{ scale: 1.02 }}
                          transition={{ duration: 0.3, ease: "easeOut" }}
                        >
                          <div
                            className="relative flex flex-col items-center justify-center h-full
                            bg-gradient-to-t from-gray-800 to-gray-900/20
                            gap-6 transition-all duration-300 group-hover:opacity-0 z-0"
                          >
                            <div className="flex items-center">
                              <h3 className="text-3xl font-black mt-2 mb-1 text-white uppercase z-10 text-center leading-none whitespace-pre-line">
                                {category.categoryName
                                  .split(" (")[0]
                                  .replace(" ", "\n")}
                              </h3>
                            </div>
                            <div className="flex items-center gap-2">
                              <div className="text-center">
                                <p className="text-xs text-white/80">
                                  ELEVATION
                                </p>
                                <p className="text-xs font-bold text-white whitespace-nowrap">
                                  {category.elevationGain || "-"}M+
                                </p>
                              </div>
                              <div className="h-6 w-px bg-white/20"></div>
                              <div className="text-center">
                                <p className="text-xs text-white/80">CUT OFF</p>
                                <p className="text-xs font-bold text-white whitespace-nowrap">
                                  {category.cot
                                    ? `${category.cot} HOURS`
                                    : "- HOURS"}
                                </p>
                              </div>
                            </div>
                         {/*    <div className="text-center mt-4">
                              <p className="text-xs font-bold text-white uppercase">
                                REGISTRATION OPEN
                              </p>
                            </div> */}
                          </div>
                          <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                            <p className="text-lg font-normal uppercase text-white">
                              View Details
                            </p>
                          </div>
                        </motion.div>
                      </Link>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          ) : (
            <motion.div
              className="flex flex-col gap-4 py-10 px-2 sm:flex-row sm:flex-nowrap sm:justify-center sm:px-4 lg:px-6"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {categories.map((category) => {
                const icons = getCategoryIcons(
                  category.categoryName,
                  category.distance
                );
                const categoryImage = getCategoryImage(category.categoryName);
                const categorySlug = getCategorySlug(category.categoryName);
                return (
                  <motion.div
                    key={`${category.distance}-${category.categoryName}`}
                    variants={cardVariants}
                    className="min-w-[90%] w-full sm:min-w-[240px] sm:flex-1 sm:flex-shrink-0"
                  >
                    <Link
                      to={`/SEATRC/${categorySlug}`}
                      className="group relative z-0 block"
                    >
                      <motion.div
                        className="relative rounded-2xl overflow-hidden bg-cover bg-center w-full h-72 z-0"
                        style={{
                          backgroundImage: `url(${categoryImage})`,
                          aspectRatio: "2/1",
                        }}
                        whileHover={{ scale: 1.02 }}
                        transition={{ duration: 0.3, ease: "easeOut" }}
                      >
                        <div
                          className="relative flex flex-col items-center justify-center h-full
                            bg-gradient-to-t from-gray-800 to-gray-900/20
                            gap-6 transition-all duration-300 group-hover:opacity-0 z-0"
                        >
                          <div className="flex items-center">
                            <h3 className="text-2xl font-black mt-2 mb-1 text-white/90 uppercase z-10 text-center leading-none whitespace-pre-line">
                              {category.categoryName
                                .split(" (")[0]
                                .replace(" ", "\n")}
                            </h3>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-center">
                              <p className="text-xs text-white/80">ELEVATION</p>
                              <p className="text-xs font-bold text-white whitespace-nowrap">
                                {category.elevationGain || "-"}M+
                              </p>
                            </div>
                            <div className="h-6 w-px bg-white/20"></div>
                            <div className="text-center">
                              <p className="text-xs text-white/80">CUT OFF</p>
                              <p className="text-xs font-bold text-white whitespace-nowrap">
                                {category.cot
                                  ? `${category.cot} HOURS`
                                  : "- HOURS"}
                              </p>
                            </div>
                          </div>
                         {/*  <div className="text-center">
                            <p className="text-xs font-bold text-white uppercase">
                              REGISTRATION OPEN
                            </p>
                          </div> */}
                        </div>
                        <div className="absolute inset-0 flex items-center justify-center bg-gray-900 bg-opacity-80 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-0">
                          <p className="text-lg font-normal uppercase text-white">
                            View Details
                          </p>
                        </div>
                      </motion.div>
                    </Link>
                  </motion.div>
                );
              })}
            </motion.div>
          )}
        </div>
      </div>
    </section>
  );
};
